import { useState } from 'react';
import { CircularProgress, Stack, Typography, Paper, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { COLORS } from '../../../utils/Application_Constants';
import StatusPill from '../../../Admin/Components/Invoicing/StatusPill';
import { invoiceStatusEnum } from '../../../Admin/Configurations/invoicingConfig';
import { openInvoicePdf } from '../../../Admin/Services/InvoiceService';
import useGetExpenseInvoiceDocuments from '../../hooks/useGetExpenseInvoiceDocuments';
import { useToast } from '../../../Admin/Contexts/ToastContext';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(4),
    },
    divider: {
        margin: theme.spacing(1, 0, 3, 0),
    },
}));

const getDisplayStatus = ({ status, isPreInvoice }) => {
    if (isPreInvoice && status === invoiceStatusEnum.Invoiced) {
        return invoiceStatusEnum.PreInvoiced;
    }
    return status;
};

const InvoiceDocumentLink = ({ document, onOpenPdf }) => {
    const [isLoading, setIsLoading] = useState(false);
    const displayStatus = getDisplayStatus(document);
    const documentType =
        document.documentType || (document.isPreInvoice ? 'Pre-Invoice' : 'Invoice');

    const handleClick = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            await onOpenPdf(document.id);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            aria-label={`Open ${documentType} ${document.invoiceNumber} PDF`}
            target="_blank"
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                color: 'inherit',
                cursor: isLoading ? 'wait' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                border: 'none',
                background: 'none',
                padding: 0,
            }}
        >
            <Stack gap={0.75}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography
                        fontSize={14}
                        fontWeight={600}
                        color={COLORS.LT_JOURNEY}
                        lineHeight="20px"
                        sx={{ textDecoration: 'underline' }}
                    >
                        {documentType} {document.invoiceNumber || '—'}
                    </Typography>

                    {displayStatus ? (
                        <StatusPill
                            status={displayStatus}
                            sx={{ minWidth: 'auto', height: 22, fontSize: 11 }}
                        />
                    ) : null}
                </Stack>
            </Stack>
        </button>
    );
};

const AssociatedInvoiceDocuments = ({ expenseId }) => {
    const classes = useStyles();
    const { showErrorToast } = useToast();
    const {
        data: documents = [],
        isLoading,
        isError,
    } = useGetExpenseInvoiceDocuments({ expenseId });

    const handleOpenPdf = async (invoiceId) => {
        try {
            await openInvoicePdf(invoiceId);
        } catch (error) {
            console.error('Error opening invoice PDF:', error);
            showErrorToast('Unable to open the invoice PDF. Please try again.');
        }
    };

    return (
        <Paper elevation={1} className={classes.paper}>
            <Typography variant="h5">
                Associated Invoices
                {!isLoading && !isError ? ` (${documents.length})` : ''}
            </Typography>
            <Divider className={classes.divider} />

            {isLoading && <CircularProgress size={24} />}

            {!isLoading && isError && (
                <Typography fontSize={14} fontWeight={400} color={COLORS.LT_MIDNIGHT50}>
                    Unable to load associated invoices
                </Typography>
            )}

            {!isLoading && !isError && documents.length === 0 && (
                <Typography fontSize={14} fontWeight={400} color={COLORS.LT_MIDNIGHT50}>
                    No associated invoices or pre-invoices
                </Typography>
            )}

            {!isLoading && !isError && documents.length > 0 && (
                <Stack gap={2}>
                    {documents.map((document) => (
                        <InvoiceDocumentLink
                            key={document.id}
                            document={document}
                            onOpenPdf={handleOpenPdf}
                        />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

export default AssociatedInvoiceDocuments;
